/**
 * CR310: S0 Scan Settings v2 - Main Component (Refactored)
 * Tabbed IA with top action bar (Reset/Save) and PaddleOCR advanced controls
 */

import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Settings, ChevronDown, ChevronUp, Cpu, Shield, Search, Brain, FileText, Maximize2 } from 'lucide-react';
import { S0ActionBar } from './S0ActionBar';
import { useS0Config } from './useS0Config';
import { useScanSettings } from '@/hooks/useScanSettings';
import {
  ScanSettingsTab,
  OcrTab,
  LargePagesTab,
  CandidateSignalsTab,
  DealLocatorTab,
  AiDeepScanTab,
} from './tabs';
import { AI_SCAN_MODELS, PRIMARY_OCR_ENGINES, type S0TabId } from './types';

interface S0ScanSettingsAccordionV2Props {
  batchRunId: string;
  currentConfig?: Record<string, unknown>;
  onConfigSaved?: () => void;
  disabled?: boolean;
}

export function S0ScanSettingsAccordionV2({
  batchRunId,
  currentConfig,
  onConfigSaved,
  disabled = false,
}: S0ScanSettingsAccordionV2Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<S0TabId>('ocr');

  const {
    config,
    isDirty: isConfigDirty,
    isSaving: isConfigSaving,
    promptPacks,
    loadingPacks,
    updateConfig,
    updatePaddleConfig,
    updateExecutionConfig,
    updateFlyerDefaults,
    updateS12OcrConfig,
    updateS12FallbackConfig,
    updateSnippetRefinement,
    handleSave: handleConfigSave,
    handleReset: handleConfigReset,
  } = useS0Config({
    batchRunId,
    currentConfig,
    onConfigSaved,
    disabled,
  });

  const {
    settings: globalSettings,
    recipes: globalRecipes,
    isLoading: isLoadingGlobal,
    isSaving: isSavingGlobal,
    isDirty: isGlobalDirty,
    updateField: updateGlobalField,
    save: saveGlobal,
    reset: resetGlobal,
  } = useScanSettings();

  const isDirty = isConfigDirty || isGlobalDirty;
  const isSaving = isConfigSaving || isSavingGlobal;

  const handleSave = async () => {
    const promises = [];
    if (isConfigDirty) promises.push(handleConfigSave());
    if (isGlobalDirty) promises.push(saveGlobal());
    await Promise.all(promises);
  };

  const handleReset = () => {
    if (isConfigDirty) handleConfigReset();
    if (isGlobalDirty) resetGlobal();
  };

  // Summary labels
  const activeSignalsCount = config.signals.filter(s => s.enabled).length;
  const modelLabel = AI_SCAN_MODELS.find(m => m.value === config.aiModel)?.shortLabel || config.aiModel;
  const ocrLabel = PRIMARY_OCR_ENGINES.find(e => e.value === config.ocrEngine)?.label || config.ocrEngine;
  const selectedPromptPack = promptPacks.find(p => p.id === config.promptPackId);
  const promptPackLabel = selectedPromptPack
    ? `${selectedPromptPack.name} v${selectedPromptPack.version}`
    : config.promptPackId
      ? `Pack ${config.promptPackId.slice(0, 8)}...`
      : null;

  return (
    <div className="space-y-2 mb-4" data-s0-scan-settings>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="border rounded-lg bg-muted/30">
          <CollapsibleTrigger asChild>
            <button
              data-s0-scan-settings-trigger
              className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="text-muted-foreground">
                  <Settings className="h-4 w-4" />
                </div>
                <div>
                  <div className="font-medium flex items-center gap-2">
                    S0: Scan Settings (v2)
                    {isDirty && (
                      <Badge variant="secondary" className="text-xs">
                        Unsaved
                      </Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Configure OCR + candidate scoring + AI deep scan for this batch run
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap justify-end">
                {/* Global Reset/Save Buttons in Header */}
                <S0ActionBar
                  isDirty={isDirty}
                  isSaving={isSaving}
                  onReset={handleReset}
                  onSave={handleSave}
                  disabled={disabled}
                />

                {/* Summary chips (collapsed view) */}
                {!isOpen && (
                  <div className="flex items-center gap-1.5 flex-wrap justify-end">
                    <Badge variant="outline" className="text-xs font-normal">
                      OCR: {ocrLabel}
                    </Badge>
                    <Badge variant={config.aiEnabled ? "outline" : "secondary"} className={`text-xs font-normal ${!config.aiEnabled ? "opacity-60" : ""}`}>
                      AI: {config.aiEnabled ? "On" : "Off"}
                    </Badge>
                    <Badge variant="outline" className={`text-xs font-normal ${!config.aiEnabled ? "opacity-60" : ""}`}>
                      Model: {config.aiEnabled ? modelLabel : "—"}
                    </Badge>
                    <Badge variant="outline" className="text-xs font-normal">
                      Threshold: {config.candidateThreshold}
                    </Badge>
                    <Badge variant="outline" className="text-xs font-normal">
                      Max AI: {config.maxAiCalls}
                    </Badge>
                    <Badge variant="outline" className="text-xs font-normal">
                      Signals: {activeSignalsCount} active
                    </Badge>
                    <Badge
                      variant={config.dealLocator.enabled ? "outline" : "secondary"}
                      className={`text-xs font-normal ${!config.dealLocator.enabled ? "opacity-60" : ""}`}
                    >
                      <Search className="h-3 w-3 mr-1" />
                      Locator: {config.dealLocator.enabled ? `On (${config.dealLocator.keywords.length} kw)` : "Off"}
                    </Badge>
                    <Badge
                      variant={promptPackLabel ? "outline" : "destructive"}
                      className="text-xs font-normal"
                    >
                      <FileText className="h-3 w-3 mr-1" />
                      Prompt: {promptPackLabel || "None"}
                    </Badge>
                  </div>
                )}
                <div className="flex items-center ml-2">
                  {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </div>
            </button>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div className="px-4 pb-4 border-t pt-4">

              {/* Tabbed Interface */}
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as S0TabId)}>
                <TabsList className="grid w-full grid-cols-6 h-auto">
                  <TabsTrigger value="scan" className="text-xs py-2 px-1">
                    <Settings className="h-3 w-3 mr-1" />
                    Scan
                  </TabsTrigger>
                  <TabsTrigger value="ocr" className="text-xs py-2 px-1">
                    <Cpu className="h-3 w-3 mr-1" />
                    OCR
                  </TabsTrigger>
                  <TabsTrigger value="large-pages" className="text-xs py-2 px-1">
                    <Maximize2 className="h-3 w-3 mr-1" />
                    Large Pages
                  </TabsTrigger>
                  <TabsTrigger value="signals" className="text-xs py-2 px-1">
                    <Shield className="h-3 w-3 mr-1" />
                    Signals
                  </TabsTrigger>
                  <TabsTrigger value="locator" className="text-xs py-2 px-1">
                    <Search className="h-3 w-3 mr-1" />
                    Locator
                  </TabsTrigger>
                  <TabsTrigger value="ai" className="text-xs py-2 px-1">
                    <Brain className="h-3 w-3 mr-1" />
                    AI Scan
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="scan" className="mt-4">
                  <ScanSettingsTab
                    config={config}
                    updateConfig={updateConfig}
                    disabled={disabled}
                    globalSettings={globalSettings}
                    globalRecipes={globalRecipes}
                    isLoadingGlobal={isLoadingGlobal}
                    isSavingGlobal={isSavingGlobal}
                    isGlobalDirty={isGlobalDirty}
                    updateGlobalField={updateGlobalField}
                    saveGlobal={saveGlobal}
                    resetGlobal={resetGlobal}
                  />
                </TabsContent>

                <TabsContent value="ocr" className="mt-4">
                  <OcrTab
                    config={config}
                    updateConfig={updateConfig}
                    updatePaddleConfig={updatePaddleConfig}
                    updateS12OcrConfig={updateS12OcrConfig}
                    updateS12FallbackConfig={updateS12FallbackConfig}
                    disabled={disabled}
                  />
                </TabsContent>

                <TabsContent value="large-pages" className="mt-4">
                  <LargePagesTab
                    config={config}
                    updateConfig={updateConfig}
                    disabled={disabled}
                  />
                </TabsContent>

                <TabsContent value="signals" className="mt-4">
                  <CandidateSignalsTab
                    config={config}
                    updateConfig={updateConfig}
                    disabled={disabled}
                  />
                </TabsContent>

                <TabsContent value="locator" className="mt-4">
                  <DealLocatorTab
                    config={config}
                    updateConfig={updateConfig}
                    disabled={disabled}
                  />
                </TabsContent>

                <TabsContent value="ai" className="mt-4">
                  <AiDeepScanTab
                    config={config}
                    updateConfig={updateConfig}
                    updateSnippetRefinement={updateSnippetRefinement}
                    promptPacks={promptPacks}
                    loadingPacks={loadingPacks}
                    disabled={disabled}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  );
}
